import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { FaFilter } from 'react-icons/fa';
import dayjs from 'dayjs';
import { frontendNotification } from '../Notification';
import * as s from './styles';

import {
  getCurrentDateTime,
  getFilterMonthsOptions,
  getFilterYearOptions,
} from '../../utils/helpers';
import Button from '../Buttons/Btn';
import MultiSelect from '../MultiSelect';
import SingleSelect from '../SingleSelect';

import useFilters from '../../hooks/useDashboardFilters';

import { customersApi, sellersApi } from '../../services/requests';
import { getOptionsFromCustomers, getOptionsFromSellers } from '../../utils/getSelectOptions';
import { monthNumberToName } from '../../utils/months';
import SelectAsyncJSX from '../SelectAsync';
import BasicDateTimePicker from '../SelectDateTime';

interface Props {
  disableYearsFilter?: boolean;
  disableMonthFilter?: boolean;
  disableSellerFilter?: boolean;
  disableCustomerFilter?: boolean;
  yearsIsSingle?: boolean;
  dateByPeriod?: boolean;
  disableDatePeriod?: boolean;
  periodType?: 'date' | 'datetime-local' | 'time';
  defaultDateRangeDaysBack?: number;
  maxDateRangeDays?: number;
  allowFutureDateRange?: boolean;
  onInitialFiltersApplied?: () => void;
}

export const DashboardFilters = ({
  disableYearsFilter = false,
  disableMonthFilter = false,
  disableSellerFilter = false,
  disableCustomerFilter = false,
  yearsIsSingle = false,
  dateByPeriod = false,
  disableDatePeriod = false,
  periodType = 'date',
  defaultDateRangeDaysBack = 93,
  maxDateRangeDays = 93,
  allowFutureDateRange = false,
  onInitialFiltersApplied,
}: Props) => {
  const today = new Date();
  const currentYear = today.getFullYear().toString();
  const currentMonth = today.getMonth() + 1;

  const defaultYearFilter = {
    label: currentYear.toString(),
    value: currentYear.toString(),
  };

  const defaultMonthFilter = {
    label: t(`${monthNumberToName(currentMonth).toLowerCase()}`),
    value: monthNumberToName(currentMonth),
  };

  const initialStartPeriod = () => {
    const date = getCurrentDateTime(defaultDateRangeDaysBack, 'sub');

    if (periodType === 'date') {
      return date.split('T')[0];
    }

    if (periodType === 'time') {
      return date.split('T')[1];
    }

    return date;
  };

  const initialEndPeriod = () => {
    const date = getCurrentDateTime();

    if (periodType === 'date') {
      return date.split('T')[0];
    }

    if (periodType === 'time') {
      return date.split('T')[1];
    }

    return date;
  };

  const [showFilter, setShowFilter] = useState(true);

  const [singlesYearsFilter, setSingleYearFilter] = useState(defaultYearFilter);
  const [yearsFilter, setYearFilter] = useState(disableYearsFilter ? [] : [defaultYearFilter]);
  const [startDateTimePicker, setStartDateTimePicker] = useState<string>(initialStartPeriod());
  const [endDateTimePicker, setEndDateTimePicker] = useState<string>(initialEndPeriod());
  const [monthsFilter, setMonthsFilter] = useState(disableMonthFilter ? [] : [defaultMonthFilter]);
  const [sellersFilter, setSellersFilter] = useState([]);
  const [customersFilter, setCustomersFilter] = useState([]);

  const { handleSetFilterState } = useFilters();

  const parseDateTimePickerValue = (value: string) => {
    if (!value) return null;

    const parsedValue = dayjs(value);

    return parsedValue.isValid() ? parsedValue : null;
  };

  const formatDateTimePickerValue = (value: any): string => {
    if (!value) return '';

    const parsedValue = dayjs(value);

    return parsedValue.isValid() ? parsedValue.format('YYYY-MM-DDTHH:mm') : '';
  };

  const resetFilters = () => {
    setYearFilter([]);
    setMonthsFilter([]);
    setSellersFilter([]);
    setCustomersFilter([]);
    setStartDateTimePicker(initialStartPeriod());
    setEndDateTimePicker(initialEndPeriod());
  };

  const filterAction = () => {
    // Validação de range máximo quando dateByPeriod está ativo
    if (dateByPeriod && startDateTimePicker && endDateTimePicker) {
      const start = new Date(startDateTimePicker);
      const end = new Date(endDateTimePicker);
      const diffMs = end.getTime() - start.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      if (diffDays > maxDateRangeDays) {
        frontendNotification({
          message: `${t('maxDateRangeExceeded')}: ${maxDateRangeDays} ${t('days')}`,
          type: 'warning',
        });
        return false;
      }

      if (diffDays < 0) {
        frontendNotification({
          message: t('startDateMustBeBeforeEndDate'),
          type: 'warning',
        });
        return false;
      }

      const now = new Date();
      if (!allowFutureDateRange && end.getTime() > now.getTime()) {
        frontendNotification({
          message: t('endDateCannotBeInTheFuture'),
          type: 'warning',
        });
        return false;
      }
    }

    let filteredYears: string[] = [];
    const filteredMonths: string[] = [];
    const filteredSellers: string[] = [];
    const filteredCustomers: string[] = [];

    if (!disableYearsFilter) {
      yearsFilter.forEach((option: any) => {
        filteredYears.push(option.value as string);
      });
    }

    if (!disableMonthFilter) {
      monthsFilter.forEach((option: any) => {
        filteredMonths.push(option.value as string);
      });
    }

    if (!disableSellerFilter) {
      sellersFilter.forEach((option: any) => {
        filteredSellers.push(option.value as string);
      });
    }

    if (!disableCustomerFilter) {
      customersFilter.forEach((option: any) => {
        filteredCustomers.push(option.value);
      });
    }

    if (yearsIsSingle) {
      filteredYears = [singlesYearsFilter.value];
    }

    handleSetFilterState({
      startPeriod: startDateTimePicker ?? null,
      endPeriod: endDateTimePicker ?? null,
      years: filteredYears,
      months: filteredMonths,
      sellers: filteredSellers,
      customers: filteredCustomers,
    });

    return true;
  };

  useEffect(() => {
    const didApplyInitialFilters = filterAction();

    if (didApplyInitialFilters) {
      onInitialFiltersApplied?.();
    }
  }, []);

  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  const maxDateTimeLocal = new Date(now.getTime() - offset)
    .toISOString()
    .slice(0, periodType === 'date' ? 10 : 16);

  return showFilter ? (
    <s.FiltersContainer>
      <div className="row">
        {dateByPeriod ? (
          <>
            <div className="inputWithLabel">
              <div className="label">{t('beginningOfPeriod') as string}</div>
              {periodType === 'datetime-local' ? (
                <BasicDateTimePicker
                  label={t('beginningOfPeriod') as string}
                  value={parseDateTimePickerValue(startDateTimePicker) as any}
                  onChange={
                    (newValue) => setStartDateTimePicker(formatDateTimePickerValue(newValue))
                  }
                  disabled={disableDatePeriod}
                  disablePast={!allowFutureDateRange}
                />
              ) : (
                <input
                  type={periodType}
                  className="input"
                  value={startDateTimePicker}
                  onChange={(e: any) => setStartDateTimePicker(e.target.value)}
                  disabled={disableDatePeriod}
                  placeholder={t('startDate')}
                  max={endDateTimePicker || (!allowFutureDateRange ? maxDateTimeLocal : undefined)}
                />
              )}
            </div>

            <div className="inputWithLabel">
              <div className="label">{t('endOfPeriod') as string}</div>
              {periodType === 'datetime-local' ? (
                <BasicDateTimePicker
                  label={t('endOfPeriod') as string}
                  value={parseDateTimePickerValue(endDateTimePicker) as any}
                  onChange={(newValue) => setEndDateTimePicker(formatDateTimePickerValue(newValue))}
                  disabled={disableDatePeriod}
                  disablePast={!allowFutureDateRange}
                />
              ) : (
                <input
                  type={periodType}
                  className="input"
                  value={endDateTimePicker}
                  onChange={(e: any) => setEndDateTimePicker(e.target.value)}
                  disabled={disableDatePeriod}
                  placeholder={t('startDate')}
                  min={startDateTimePicker}
                  max={!allowFutureDateRange ? maxDateTimeLocal : undefined}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {yearsIsSingle ? (
              <div className="inputWithLabel">
                <div className="label">{t('year') as string}</div>
                <SingleSelect
                  className="singles-select-item"
                  placeholder={t('filterYear') as string}
                  options={getFilterYearOptions()}
                  value={singlesYearsFilter}
                  onChange={(e) => setSingleYearFilter(e)}
                  isDisabled={disableYearsFilter}
                />
              </div>
            ) : (
              <div className="inputWithLabel">
                <div className="label">{t('years') as string}</div>
                <MultiSelect
                  options={getFilterYearOptions()}
                  value={yearsFilter}
                  onChange={setYearFilter}
                  placeholder={t('filterYears')}
                  disabled={disableYearsFilter}
                />
              </div>
            )}
            <div className="inputWithLabel">
              <div className="label">{t('months') as string}</div>
              <MultiSelect
                labelledBy="months-list"
                options={getFilterMonthsOptions()}
                value={monthsFilter}
                onChange={setMonthsFilter}
                placeholder={t('filterMonhts')}
                disabled={disableMonthFilter}
              />
            </div>
          </>
        )}

        <div className="inputWithLabel">
          <div className="label">{t('sellers') as string}</div>
          <SelectAsyncJSX
            value={sellersFilter}
            onChange={setSellersFilter}
            request={sellersApi}
            reqResponseToOption={getOptionsFromSellers}
            placeholder={t('sellers')}
            isMulti
            disabled={disableSellerFilter}
          />
        </div>

        <div className="inputWithLabel">
          <div className="label">{t('customers') as string}</div>
          <SelectAsyncJSX
            value={customersFilter}
            onChange={setCustomersFilter}
            request={customersApi}
            reqResponseToOption={getOptionsFromCustomers}
            placeholder={t('customers')}
            isMulti
            disabled={disableCustomerFilter}
          />
        </div>
      </div>

      <div className="global-filter-actions-area">
        <Button type="text" text={t('clearFilters')} onClick={resetFilters} />
        |
        <Button type="text" text={t('hideFilters')} onClick={() => setShowFilter(!showFilter)} />
        <Button text={t('filter')} onClick={filterAction} />
      </div>
    </s.FiltersContainer>
  ) : (
    <s.ButtonAreaShowFilters>
      <div>
        <Button
          text={t('showFilters')}
          Icon={FaFilter}
          onClick={() => setShowFilter(!showFilter)}
        />
      </div>
    </s.ButtonAreaShowFilters>
  );
};

export default DashboardFilters;
